import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Info, Trash2, CheckCheck, Gift, Calendar, AlertTriangle, DollarSign, CheckSquare, ChevronRight, Users, Heart } from 'lucide-react';
import { useNotification } from '../contexts/NotificationContext';
import { DESIGN_TOKENS } from '../constants/designTokens';
import AppBadge from './ui/AppBadge';
import AppButton from './ui/AppButton';
import AppCard from './ui/AppCard';
import AppTabs from './ui/AppTabs';

// Category Icons and Colors
const CATEGORY_CONFIG = {
    birthday: { icon: Gift, color: 'bg-pink-100 text-pink-600', label: '생일' },
    schedule: { icon: Calendar, color: 'bg-blue-100 text-blue-600', label: '일정' },
    pastoral: { icon: AlertTriangle, color: 'bg-amber-100 text-amber-600', label: '목양' },
    budget: { icon: DollarSign, color: 'bg-emerald-100 text-emerald-600', label: '재정' },
    checklist: { icon: CheckSquare, color: 'bg-purple-100 text-purple-600', label: '체크리스트' },
    activity: { icon: Users, color: 'bg-indigo-100 text-indigo-600', label: '활동' },
    cheer: { icon: Heart, color: 'bg-rose-100 text-rose-600', label: '격려' }, // [NEW]
    system: { icon: Info, color: 'bg-gray-100 text-gray-600', label: '시스템' }
};

const NOTIFICATION_TABS = [
    { id: 'all', label: '전체' },
    { id: 'activity', label: '📋 활동' },
    { id: 'cheer', label: '💝 격려' },
    { id: 'birthday', label: '🎂 생일' },
    { id: 'schedule', label: '📅 일정' },
    { id: 'pastoral', label: '⚠️ 목양' },
    { id: 'budget', label: '💰 재정' }
];

export default function NotificationDropdown({ onClose, anchorRef, onNavigate, onOpenMemberDetail }) {
    const { notifications, markAsRead, markAllAsRead, clearAllNotifications, unreadCount, showToast } = useNotification();
    const dropdownRef = useRef(null);
    const [position, setPosition] = useState(null);
    const [activeTab, setActiveTab] = useState('all'); // 'all', 'birthday', 'schedule', 'pastoral', 'budget', 'cheer'
    const brand = DESIGN_TOKENS.brand;

    // 위치 계산 (useLayoutEffect로 깜빡임 방지)
    useLayoutEffect(() => {
        if (anchorRef?.current) {
            const updatePosition = () => {
                const rect = anchorRef.current.getBoundingClientRect();
                const isMobile = window.innerWidth < 768;

                if (isMobile) {
                    // Mobile: Full width dropdown below header
                    setPosition({
                        top: rect.bottom + 8,
                        left: 16,
                        right: 16,
                        isMobile: true
                    });
                } else {
                    // Desktop: Right-aligned dropdown
                    setPosition({
                        top: rect.bottom + 10,
                        right: window.innerWidth - rect.right,
                        isMobile: false
                    });
                }
            };
            updatePosition();
            window.addEventListener('resize', updatePosition);
            return () => window.removeEventListener('resize', updatePosition);
        }
    }, [anchorRef]);

    // 외부 클릭 감지
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                anchorRef?.current && !anchorRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose, anchorRef]);

    // ESC 키로 닫기
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    // 날짜 포맷팅 (방금 전, 1분 전 등)
    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = (now - date) / 1000; // 초 단위

        if (diff < 60) return '방금 전';
        if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
        if (diff < 604800) return `${Math.floor(diff / 86400)}일 전`;
        return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
    };

    // Filter notifications by tab
    const filteredNotifications = activeTab === 'all'
        ? notifications
        : notifications.filter(n => n.category === activeTab);

    const extractStudentNameFromText = (text = '') => {
        const normalizedText = String(text || '')
            .replace(/['"`]/g, '')
            .trim();
        if (!normalizedText) return null;
        const matched = normalizedText.match(/([가-힣A-Za-z0-9]{2,20})\s*님/);
        return matched?.[1]?.trim() || null;
    };

    const extractQuotedNameFromText = (text = '') => {
        const normalizedText = String(text || '').trim();
        if (!normalizedText) return null;
        const matched = normalizedText.match(/['"]([가-힣A-Za-z0-9]{2,20})['"]/);
        return matched?.[1]?.trim() || null;
    };

    const extractTeacherNameFromText = (text = '') => {
        const normalizedText = String(text || '')
            .replace(/['"`]/g, '')
            .trim();
        if (!normalizedText) return null;
        const matched = normalizedText.match(/([가-힣A-Za-z0-9]{2,20})\s*(선생님|교사|동역자)/);
        return matched?.[1]?.trim() || null;
    };

    const getPastoralTarget = (notif) => {
        if (notif.targetId) return notif.targetId;

        const nameFromMessage = extractStudentNameFromText(notif.message);
        if (nameFromMessage) {
            return { kind: 'studentName', value: nameFromMessage };
        }

        const nameFromTitle = extractStudentNameFromText(notif.title);
        if (nameFromTitle) {
            return { kind: 'studentName', value: nameFromTitle };
        }

        return null;
    };

    const resolveStudentTarget = (notif) => {
        if (notif.targetId) return notif.targetId;
        if (notif.category === 'pastoral') return getPastoralTarget(notif);

        const nameFromMessage = extractStudentNameFromText(notif.message) || extractQuotedNameFromText(notif.message);
        if (nameFromMessage) {
            return { kind: 'studentName', value: nameFromMessage };
        }

        const nameFromTitle = extractStudentNameFromText(notif.title) || extractQuotedNameFromText(notif.title);
        if (nameFromTitle) {
            return { kind: 'studentName', value: nameFromTitle };
        }

        return null;
    };

    const resolveTeacherTarget = (notif) => {
        if (notif.targetId) return notif.targetId;

        const nameFromMessage =
            extractTeacherNameFromText(notif.message) ||
            extractStudentNameFromText(notif.message) ||
            extractQuotedNameFromText(notif.message);
        if (nameFromMessage) {
            return { kind: 'teacherName', value: nameFromMessage };
        }

        const nameFromTitle =
            extractTeacherNameFromText(notif.title) ||
            extractStudentNameFromText(notif.title) ||
            extractQuotedNameFromText(notif.title);
        if (nameFromTitle) {
            return { kind: 'teacherName', value: nameFromTitle };
        }

        return null;
    };

    const isTeacherActivityNotification = (notif) => {
        const text = `${notif?.title || ''} ${notif?.message || ''}`;
        return /선생님|동역자|교사/.test(text);
    };

    const isStudentActivityNotification = (notif) => {
        const text = `${notif?.title || ''} ${notif?.message || ''}`;
        return /학생|명단/.test(text);
    };

    const isLegacyActivityNotification = (notif) => {
        const version = Number(notif?.notificationVersion || 0);
        return !Number.isFinite(version) || version < 2;
    };

    const resolveGenericMemberTarget = (notif) => {
        if (notif.targetId) return notif.targetId;

        const nameFromMessage =
            extractTeacherNameFromText(notif.message) ||
            extractStudentNameFromText(notif.message) ||
            extractQuotedNameFromText(notif.message);
        if (nameFromMessage) {
            return { kind: 'name', value: nameFromMessage };
        }

        const nameFromTitle =
            extractTeacherNameFromText(notif.title) ||
            extractStudentNameFromText(notif.title) ||
            extractQuotedNameFromText(notif.title);
        if (nameFromTitle) {
            return { kind: 'name', value: nameFromTitle };
        }

        return null;
    };

    const resolveMemberDetailTarget = (notif) => {
        if (notif?.targetMenu === 'students') {
            const target = resolveStudentTarget(notif);
            return target ? { menu: 'students', target } : null;
        }

        if (notif?.targetMenu === 'teachers') {
            const target = resolveTeacherTarget(notif);
            return target ? { menu: 'teachers', target } : null;
        }

        if (notif?.category === 'pastoral') {
            const target = resolveStudentTarget(notif);
            return target ? { menu: 'students', target } : null;
        }

        if (notif?.category === 'activity' && isLegacyActivityNotification(notif)) {
            if (isTeacherActivityNotification(notif)) {
                const target = resolveTeacherTarget(notif);
                return target ? { menu: 'teachers', target } : null;
            }
            if (isStudentActivityNotification(notif)) {
                const target = resolveStudentTarget(notif);
                return target ? { menu: 'students', target } : null;
            }
        }

        if (notif?.category === 'system') {
            const target = resolveGenericMemberTarget(notif);
            return target ? { menu: 'member', target } : null;
        }

        return null;
    };

    const canHandleNotificationClick = (notif) => {
        const memberDetailTarget = resolveMemberDetailTarget(notif);
        if (memberDetailTarget && onOpenMemberDetail) return true;
        if (!onNavigate) return false;

        if (notif.targetMenu) return true;

        switch (notif.category) {
            case 'birthday':
            case 'schedule':
            case 'checklist':
            case 'budget':
                return Boolean(notif.targetId);
            case 'pastoral':
                return Boolean(resolveStudentTarget(notif));
            case 'activity':
                if (!isLegacyActivityNotification(notif)) return false;
                if (isTeacherActivityNotification(notif)) return true;
                if (isStudentActivityNotification(notif)) return true;
                return false;
            default:
                return false;
        }
    };

    // Handle notification click
    const handleNotificationClick = (notif) => {
        markAsRead(notif.id);
        const canInteract = canHandleNotificationClick(notif);
        if (!canInteract) {
            showToast("열 수 있는 상세 대상이 없는 알림입니다.", "info");
            return;
        }

        const memberDetailTarget = resolveMemberDetailTarget(notif);
        if (memberDetailTarget && onOpenMemberDetail) {
            onOpenMemberDetail(memberDetailTarget.menu, memberDetailTarget.target);
            onClose();
            return;
        }

        // Navigate based on category
        if (onNavigate) {
            if (notif.targetMenu) {
                const targetForMenu = notif.targetMenu === 'students'
                    ? resolveStudentTarget(notif)
                    : notif.targetId;
                onNavigate(notif.targetMenu, targetForMenu);
                onClose();
                return;
            }

            switch (notif.category) {
                case 'birthday':
                    if (!notif.targetId) break;
                    onNavigate('students', notif.targetId);
                    break;
                case 'pastoral': {
                    const pastoralTarget = resolveStudentTarget(notif);
                    if (!pastoralTarget) break;
                    onNavigate('students', pastoralTarget);
                    break;
                }
                case 'schedule':
                case 'checklist':
                    if (!notif.targetId) break;
                    onNavigate('schedule', notif.targetId);
                    break;
                case 'budget':
                    if (!notif.targetId) break;
                    onNavigate('finance', notif.targetId);
                    break;
                case 'cheer':
                    // [NEW] 격려 알림 클릭 시 추가 동작? (현재는 단순 읽음 처리)
                    break;
                case 'activity':
                    if (!isLegacyActivityNotification(notif)) break;
                    if (isTeacherActivityNotification(notif)) {
                        onNavigate('teachers', notif.targetId || null);
                        break;
                    }
                    if (isStudentActivityNotification(notif)) {
                        onNavigate('students', notif.targetId || null);
                    }
                    break;
                default:
                    break;
            }
            onClose();
            return;
        }
    };

    // Get category config
    const getCategoryConfig = (category) => {
        return CATEGORY_CONFIG[category] || CATEGORY_CONFIG.system;
    };

    if (!position) return null;

    const dropdownStyle = position.isMobile
        ? { top: position.top, left: position.left, right: position.right }
        : { top: position.top, right: position.right };

    return createPortal(
        <AppCard
            ref={dropdownRef}
            className={`fixed rounded-2xl overflow-hidden animate-fade-in-up z-[9999] ${position.isMobile ? 'w-auto' : 'w-96'}`}
            style={dropdownStyle}
        >
            {/* 헤더 */}
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-white to-gray-50/50">
                <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-xl ${brand.primaryLightBgClass} flex items-center justify-center`}>
                        <Bell size={16} className={brand.primaryTextClass} />
                    </div>
                    <h3 className="font-bold text-gray-800">알림 센터</h3>
                    {unreadCount > 0 && (
                        <AppBadge variant="danger" size="sm" className="animate-pulse">
                            {unreadCount}
                        </AppBadge>
                    )}
                </div>
                <div className="flex gap-1">
                    <AppButton
                        onClick={markAllAsRead}
                        variant="icon"
                        size="icon"
                        title="모두 읽음 처리"
                    >
                        <CheckCheck size={16} />
                    </AppButton>
                    <AppButton
                        onClick={clearAllNotifications}
                        variant="dangerIcon"
                        size="icon"
                        title="모두 지우기"
                    >
                        <Trash2 size={16} />
                    </AppButton>
                </div>
            </div>

            {/* 탭 필터 */}
            <AppTabs tabs={NOTIFICATION_TABS} activeTab={activeTab} onChange={setActiveTab} />

            {/* 알림 목록 */}
            <div className="max-h-[50vh] overflow-y-auto custom-scrollbar">
                {filteredNotifications.length === 0 ? (
                    <div className="py-16 text-center text-gray-400 flex flex-col items-center gap-3">
                        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                            <Bell size={28} className="opacity-30" />
                        </div>
                        <div>
                            <p className="font-bold text-gray-500">새로운 알림이 없습니다</p>
                            <p className="text-xs mt-1">생일, 일정, 목양 알림이 여기에 표시됩니다</p>
                        </div>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-50">
                        {filteredNotifications.map(notif => {
                            const config = getCategoryConfig(notif.category);
                            const IconComponent = config.icon;
                            const canInteract = canHandleNotificationClick(notif);

                            return (
                                <div
                                    key={notif.id}
                                    onClick={() => handleNotificationClick(notif)}
                                    className={`p-4 transition flex gap-3 group ${canInteract ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default bg-gray-50/40'} ${!notif.read ? 'bg-blue-50/30' : ''}`}
                                >
                                    <div className={`mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${config.color} transition-transform group-hover:scale-110`}>
                                        <IconComponent size={16} strokeWidth={2.5} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-1">
                                            <div className="flex items-center gap-2">
                                                <h4 className={`text-sm font-bold truncate ${!notif.read ? 'text-gray-900' : 'text-gray-600'}`}>
                                                    {notif.title}
                                                </h4>
                                                <AppBadge size="xs" className={config.color}>
                                                    {config.label}
                                                </AppBadge>
                                            </div>
                                            {!notif.read && <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 flex-shrink-0 animate-pulse"></div>}
                                        </div>
                                        <p className="text-xs text-gray-500 line-clamp-2 mb-1.5 leading-relaxed">
                                            {notif.message}
                                        </p>
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] text-gray-400 font-medium">
                                                {formatTimeAgo(notif.date)}
                                            </span>
                                            {canInteract && (
                                                <span className={`text-[10px] ${brand.primaryTextClass} font-bold flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity`}>
                                                    자세히 보기 <ChevronRight size={10} />
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* 푸터 */}
            {notifications.length > 0 && (
                <div className="p-3 border-t border-gray-100 bg-gray-50/50">
                    <p className="text-center text-[10px] text-gray-400">
                        최근 {notifications.length}개의 알림 · 최대 50개 저장
                    </p>
                </div>
            )}
        </AppCard>,
        document.body
    );
}
